import React, {CSSProperties, useRef, useState} from "react";
import {Alert, Container, ListGroup, Tooltip} from 'react-bootstrap'
import {ReactComponent as Placeholder} from '../image/image.svg'
import {ComponentWithTooltip} from "./ComponentWithTooltip";
import {TileCategory, Tile} from "../model";
import {useKeycloak} from "@react-keycloak/web";
import {ExternalLink} from "lucide-react";
import {useNavigate} from "react-router-dom";
import {AccessLogRepository} from "../repository";
import {Page} from "./Page";

type GalleryProps = {
    categories: TileCategory[],
}

export const Gallery = (props: GalleryProps) => {

    return (
        <div style={{ minHeight: '100%', display: 'grid', gridTemplateRows: '5rem 1fr', width: '100%'}}>
            <div className={'d-flex-center position-relative'}>
                <div
                    className={`ff-netto bg-light text-primary text-center m-0 fs-3 w-100`}
                    // className={`ff-netto bg-primary swatch-primary text-center m-0 fs-3 w-100`}
                    style={{
                        lineHeight: '5rem',
                        height: '5rem',
                        display: 'inline-block',
                        padding: '0 2rem',
                        whiteSpace: "nowrap",
                        boxShadow: "0 .5rem 1rem rgba(0,0,0,.3)"
                    }}
                >
                    {`Bienvenue sur le portail du Territoire d'Énergie Orne`}
                </div>
            </div>

            <Container className={'d-flex-center flex-column py-2'}>
                <div
                    className={'d-grid gap-4 w-100 '}
                    style={{
                        gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))',
                    }}
                >
                    { props.categories.map((category, index) => <GalleryChapter key={index} category={category!}/>) }
                </div>
            </Container>
        </div>
    )
}

type GalleryChapterProps = {
    category: TileCategory
}

const GalleryChapter = (props: GalleryChapterProps) => {
    return (
        <>
            <div
                className={`ff-titillium p-1 ps-2 rounded`}
                style={{
                    gridColumn: '1/-1',
                    // backgroundColor: 'rgba(255,255,255,.5)'
                }}
            >
                <div className={`text-primary fs-4`}>{props.category.name}</div>
                {props.category.description && <div>{props.category.description}</div>}
            </div>
            {
                props.category.tiles.map((tile, index) => <GalleryTile key={index} index={index} tile={tile}/>)
            }
        </>
    );
}

type GalleryTileProps = {
    tile: Tile,
    index: number
}

export const GalleryTile = (props: GalleryTileProps) => {

    const tooltip = () : JSX.Element | null => {

        const state = props.tile.state;

        if (state?.description || props.tile.description)
        {
            return <Tooltip>
                {
                    state?.description && <div className={`fw-bold`}>{state.description}</div>
                }
                <div>
                    {props.tile.description}
                </div>
            </Tooltip>
        }

        return null;
    }

    let style: CSSProperties = {};
    const state = props.tile.state;

    style.color = state?.color
    style.border = `1px solid ${state?.color}`

    const now = new Date().getTime();

    // if (state?.disable) {
    //     return (
    //         <ComponentWithTooltip tooltip={tooltip()}>
    //             <div
    //                 className={`gallery-tile disabled rounded rounded-4 ff-netto`}
    //                 style={{ ...style, fontSize: '1.2rem' }}
    //                 key={props.index}
    //             >
    //                 {
    //                     props.tile.imageName !== undefined ?
    //                         <img
    //                             alt={props.tile.name}
    //                             src={`${process.env.REACT_APP_SERVER_URL}/images/tiles/${props.tile.imageName}?${now}`}
    //                             style={{
    //                                 height: '50%',
    //                                 width: 'auto',
    //                                 maxWidth: '90%'
    //                             }}
    //                         />
    //                         :
    //                         <Placeholder
    //                             style={{
    //                                 height: '50%',
    //                                 width: 'auto',
    //                                 maxWidth: '90%'
    //                             }}
    //                         />
    //                 }
    //                 <div>{props.tile.name.split('#').map(row => <div>{row.trim()}</div>)}</div>
    //             </div>
    //         </ComponentWithTooltip>
    //     )
    // }

    return (
        <LoggedTile {...props} now={now} style={style} key={props.tile.id} tooltip={tooltip()} disabled={state?.disable}/>
    )
}

type LoggedTileProps = GalleryTileProps & {
    now?: number,
    style?: CSSProperties,
    disabled?: boolean,
    tooltip?: JSX.Element | null
}

export const LoggedTile = (props: LoggedTileProps) => {

    const { keycloak } = useKeycloak()

    const [x, setX] = useState<number|null>(null)
    const [y, setY] = useState<number|null>(null)
    const [width, setWidth] = useState<number|undefined>(undefined)
    const [visibleContextMenu, setVisibleContextMenu] = useState<boolean>(false)
    const [accessLogLoading, setAccessLogLoading] = useState(false)

    // handling mobile events
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
    const longPressTriggered = useRef(false); // Pour éviter un double déclenchement
    const LONG_PRESS_DURATION = 500; // Temps en ms pour un appui long

    const repository = new AccessLogRepository()

    const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
        if (props.disabled) { return }
        if (event.type === "TouchEvent") {
            setX(0)
            setWidth(window.innerWidth)
        }
        else {
            setX(event.pageX)
            setWidth(undefined)
        }
        setY(event.pageY)
        setVisibleContextMenu(true)
    }

    const handleMouseDown = (event: React.MouseEvent<HTMLElement>) => {
        if (props.disabled) { return; }
        event.preventDefault();
        event.stopPropagation();

        if (accessLogLoading) {
            return;
        }

        switch (event.button) {
            case 0: // left button
                handleNavigation()
                break;
            case 1: // middle button
                handleNavigation(true)
                break;
            case 2: // right button
            default:
                handleContextMenu(event)
                break;
        }
    }

    const handleTouchStart = (e: React.TouchEvent) => {
        if (props.disabled) { return }
        const touch = e.touches[0]; // Prendre le premier doigt
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
        longPressTriggered.current = false; // Réinitialiser l'état d'appui long

        // Démarrer un timer pour détecter l'appui long
        longPressTimerRef.current = setTimeout(() => {
            longPressTriggered.current = true; // Marquer que l'appui long a été déclenché

            // Simuler un clic droit
            const simulatedEvent = {
                preventDefault: () => {},
                stopPropagation: () => {},
                pageX: touch.pageX,
                pageY: touch.pageY,
                type: 'TouchEvent'
            } as React.MouseEvent<HTMLElement>;

            handleContextMenu(simulatedEvent)
        }, LONG_PRESS_DURATION);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (props.disabled) { return; }
        if (!touchStartRef.current) return;

        const touch = e.touches[0];
        const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
        const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);

        if (deltaX > 10 || deltaY > 10) {
            // Si l'utilisateur bouge trop, on annule tout
            if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
            touchStartRef.current = null;
        }
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (props.disabled) { return; }
        if (!touchStartRef.current) return;

        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
        }

        // Si l'appui long a été déclenché, on ignore l'appui court
        if (!longPressTriggered.current) {
            handleNavigation();
        }

        touchStartRef.current = null;
    };

    const handleNavigation = (newTab?: boolean) => {
        if (props.disabled) { return; }

        if (accessLogLoading) { return; }

        const tokenData = keycloak.tokenParsed;

        const groups = tokenData?.groups
        const username = tokenData?.preferred_username
        const name = tokenData?.name
        const date = new Date()
            .toLocaleString('sv-SE', {
                timeZone: 'Europe/Paris'
            });
        const tile = props.tile.id
        const tileName = props.tile.name
        const url = props.tile.url
        const userAgent = navigator.userAgent

        setAccessLogLoading(true)

        repository
            .create({
                id: 0,
                url: `${url}`,
                username: username,
                tileId: tile,
                name: name,
                authGroups: groups,
                date: date,
                userAgent: userAgent,
                tileName: tileName
            })
            .finally(() => {
                setAccessLogLoading(false)
                if (url) {
                    if (newTab) {
                        window.open(url, "_blank")
                    }
                    else {
                        window.location.href = url
                    }
                }
            })
    }

    return (
        <ComponentWithTooltip tooltip={props.tooltip || null}>
            <div
                onMouseOver={(e) => console.log(e)}
                onMouseDown={(e) => handleMouseDown(e)}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onContextMenu={(e) => {e.preventDefault(); e.stopPropagation();}}
                onAuxClick={(e) => {e.preventDefault(); e.stopPropagation();}}
                onClick={(e) => {e.preventDefault(); e.stopPropagation();}}
                className={`gallery-tile rounded rounded-4 ff-netto ${accessLogLoading ? 'tile-busy' : ''}`}
                style={{ ...props.style, fontSize: '1.2rem' }}
                key={props.index}
            >
                <TileContextMenu
                    x={x}
                    y={y}
                    width={width}
                    visible={visibleContextMenu}
                    onClose={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
                        setVisibleContextMenu(false)
                    }}
                    onNavigate={() => handleNavigation(true)}
                />
                {
                    props.tile.imageName !== undefined ?
                        <img
                            alt={props.tile.name}
                            src={`${process.env.REACT_APP_SERVER_URL}/images/tiles/${props.tile.imageName}?${props.now}`}
                            style={{
                                height: '50%',
                                width: 'auto',
                                maxWidth: '90%'
                            }}
                        />
                        :
                        <Placeholder
                            style={{
                                height: '50%',
                                width: 'auto',
                                maxWidth: '90%'
                            }}
                        />
                }
                <div>{props.tile.name.split('#').map((row, index) => <div key={index}>{row.trim()}</div>)}</div>
            </div>
        </ComponentWithTooltip>
    )
}

type TileContextMenuProps = {
    x: number | null,
    y: number | null,
    width?: number,
    visible: boolean,
    onClose: (e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => void,
    onNavigate: () => void
}

const TileContextMenu = (props: TileContextMenuProps) => {

    const { x, y, width, visible, onClose, onNavigate } = props;

    if (!visible) {
        return null;
    }

    return (
        <div
            className={'context-backdrop no-select'}
            onMouseDown={(e) => onClose(e)}
            onTouchEnd={(e) => onClose(e)}
        >
            <div
                className={'context-menu'}
                style={{
                    position: "absolute",
                    left: x || 0,
                    top: y || 0,
                    width: width
                }}
            >
                <ListGroup className={`context-items`}>
                    <ListGroup.Item onMouseDown={onNavigate} onTouchEnd={onNavigate}>
                        <ExternalLink/>
                        {`Ouvrir dans un nouvel onglet`}
                    </ListGroup.Item>
                </ListGroup>
            </div>
        </div>
    )
}